import { useEffect, useRef } from "react";
import { canvasDpr } from "@/lib/canvas";
import { formK, stepBrain } from "@/lib/brainScene";

const C = "0, 229, 255";
const CAM = 3.4;

export const AssembleCanvas = ({ dim, build, timing, scale = 0.34, testId }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const host = canvas.parentElement;
    const target = { x: 0, y: 0 }, par = { x: 0, y: 0 };
    let w = 0, h = 0, mobile = false, brain = null, raf = null, onScreen = true, resizeTimer = null;
    let t = 0, last = null;

    const init = (rebuild) => {
      const dpr = canvasDpr();
      const rect = host.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      mobile = w < 768;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (rebuild) brain = build(mobile);
    };

    const project = (x, y, z, ang, tilt, R, cx, cy) => {
      const ca = Math.cos(ang), sa = Math.sin(ang);
      const x1 = x * ca + z * sa, z1 = -x * sa + z * ca;
      const ct = Math.cos(tilt), st = Math.sin(tilt);
      const y2 = y * ct - z1 * st, z2 = y * st + z1 * ct;
      const s = CAM / (CAM + z2);
      return [cx + x1 * R * s, cy - y2 * R * s, z2, s];
    };

    const frame = () => {
      par.x += (target.x - par.x) * 0.04;
      par.y += (target.y - par.y) * 0.04;
      const R = Math.min(w, h) * scale * (mobile ? 1.06 : 1);
      const cx = w / 2, cy = h / 2;
      const ang = 0.9 + t * 0.14 + par.x * 0.25;
      const tilt = 0.18 + par.y * 0.12;
      const formed = Math.min(Math.max((t - timing.formedAt + 1.2) / 1.2, 0), 1);

      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, w, h);
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, "rgba(5,5,5,0.97)");
      bg.addColorStop(0.85, "rgba(5,5,5,0.97)");
      bg.addColorStop(1, "rgba(5,5,5,0)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.4);
      halo.addColorStop(0, `rgba(${C}, ${0.07 * formed})`);
      halo.addColorStop(1, `rgba(${C}, 0)`);
      ctx.fillStyle = halo;
      ctx.fillRect(cx - R * 1.5, cy - R * 1.5, R * 3, R * 3);

      const pos = brain.nodes.map((n) => {
        const k = formK(n, t, timing);
        return [...project(n.fx + (n.tx - n.fx) * k, n.fy + (n.ty - n.fy) * k, n.fz + (n.tz - n.fz) * k, ang, tilt, R, cx, cy), k];
      });
      ctx.globalCompositeOperation = "lighter";
      ctx.lineWidth = 0.6;

      if (formed < 1) {
        const link = mobile ? 70 : 90, fade = 1 - formed;
        brain.floatLinks.forEach(([i, j]) => {
          const dx = pos[i][0] - pos[j][0], dy = pos[i][1] - pos[j][1];
          if (Math.abs(dx) > link || Math.abs(dy) > link) return;
          const d = Math.hypot(dx, dy);
          if (d < link) {
            ctx.strokeStyle = `rgba(${C}, ${(1 - d / link) * 0.12 * fade})`;
            ctx.beginPath();
            ctx.moveTo(pos[i][0], pos[i][1]);
            ctx.lineTo(pos[j][0], pos[j][1]);
            ctx.stroke();
          }
        });
      }
      if (formed > 0) {
        brain.links.forEach(([i, j]) => {
          const a = pos[i], b = pos[j];
          const depth = Math.min(Math.max(0.55 - (a[2] + b[2]) * 0.35, 0.15), 1);
          const glow = (brain.nodes[i].glow + brain.nodes[j].glow) / 2;
          ctx.strokeStyle = `rgba(${C}, ${(0.09 + glow * 0.35) * depth * formed})`;
          ctx.beginPath();
          ctx.moveTo(a[0], a[1]);
          ctx.lineTo(b[0], b[1]);
          ctx.stroke();
        });
      }
      brain.nodes.forEach((n, i) => {
        const [x, y, z, s] = pos[i];
        const depth = Math.min(Math.max(0.6 - z * 0.45, 0.2), 1);
        const a = (0.45 + n.glow * 0.55) * depth;
        const r = (1 + n.glow * 1.2) * s;
        if (n.glow > 0.05 || (!mobile && i % 2 === 0)) {
          ctx.fillStyle = `rgba(${C}, ${a * 0.18})`;
          ctx.beginPath();
          ctx.arc(x, y, r * 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = `rgba(${C}, ${a})`;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      });
      brain.pulses.forEach((p) => {
        const a = pos[p.from], b = pos[p.to];
        const x = a[0] + (b[0] - a[0]) * p.t, y = a[1] + (b[1] - a[1]) * p.t;
        ctx.fillStyle = `rgba(${C}, 0.3)`;
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${C}, 0.95)`;
        ctx.beginPath();
        ctx.arc(x, y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalCompositeOperation = "source-over";
    };

    const loop = (now) => {
      const dt = last === null ? 0 : Math.min((now - last) / 1000, 0.05);
      last = now;
      t += dt;
      stepBrain(brain, dt, mobile ? 8 : 16, t > timing.formedAt);
      frame();
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (reduce || raf || document.hidden || !onScreen) return;
      last = null;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };
    const staticFrame = () => {
      t = timing.formedAt + 2;
      frame();
    };
    const onMove = (e) => {
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        init(false);
        if (reduce) staticFrame();
      }, 150);
    };

    init(true);
    if (reduce) staticFrame();
    else start();

    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      onScreen ? start() : stop();
    });
    io.observe(host);
    if (!reduce) window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      clearTimeout(resizeTimer);
      io.disconnect();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 block pointer-events-none"
      style={{ opacity: dim ? 0.78 : 1, transition: "opacity 1.6s ease" }}
      aria-hidden="true"
      data-testid={testId}
    />
  );
};
